import React, { useEffect, useState } from "react";
import Header from "../WelcomeHeader";
import { ReactComponent as IncompleteImg } from "../../../Assets/Images/incompleteImg.svg";
import PrimaryButton from "../PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import { createConnectedAccount, getOnboardingStatus } from "../../../services/fundraiserService";
import { useNavigate } from "react-router-dom";
import { getFcmToken } from "../../../utils/connectStore";
import { clearAuth } from "../../../redux/slices/authSlice";
import Loader from "../Loader";
import axios from "../../../utils/axios";

const CompleteSetup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [isChecking, setIsChecking] = useState(true);

  // If user returns from Stripe (or just opened this page), re-check onboarding
  // and redirect appropriately - clear auth and send to fundraiser login if Stripe is complete
  useEffect(() => {
    let timeoutId;
    const refresh = async () => {
      // First check if user has a token - if not, they shouldn't be here
      if (!token) {
        setIsChecking(false);
        navigate("/fundraiser/account-login", { replace: true });
        return;
      }

      try {
        const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
        const detailsSubmitted = statusPromise?.data?.detailsSubmitted === true;
        const chargesEnabled = statusPromise?.data?.chargesEnabled === true;
        
        // Check if Stripe onboarding is complete
        if (detailsSubmitted && chargesEnabled) {
          // Stripe complete - check fundraiser approval status
          // Use explicit token like in Login page to ensure it works correctly
          try {
            const checkResponse = await axios.get("fundraiser/profile", {
              headers: { 
                Authorization: `Bearer ${token}`,
                "api-key": process.env.REACT_APP_API_KEY
              }
            });
            
            const fundraiserProfile = checkResponse?.data?.data;
            
            // Check admin approval status from the profile
            const profileStatus = typeof fundraiserProfile?.status === "string" 
              ? fundraiserProfile.status.toLowerCase() 
              : fundraiserProfile?.status;
            const isApproved = profileStatus === "approved";

            if (isApproved) {
              // Approved AND Stripe complete - navigate to dashboard (dashboard will verify again on mount)
              navigate("/fundraiser/dashboard", { replace: true });
              return; // Exit early, don't render page
            } else {
              // Stripe complete but NOT approved - clear auth and redirect (no logout API call needed)
              dispatch(clearAuth());
              // Use sessionStorage to persist modal flag across page reloads (Stripe redirect causes full page reload)
              sessionStorage.setItem('showAccountPendingModal', 'true');
              // Use replace to prevent back navigation
              navigate("/fundraiser/account-login", { 
                replace: true,
                state: { 
                  message: "Your account setup is complete! Your account is pending admin approval. You will be notified once approved.",
                  showAccountPendingModal: true // Flag to show modal for newly created fundraiser
                }
              });
              return; // Exit early, don't render page
            }
          } catch (profileError) {
            console.error("Failed to fetch fundraiser profile:", profileError);
            // If profile fetch fails, clear auth and redirect (no logout API call needed)
            dispatch(clearAuth());
            navigate("/fundraiser/account-login", { 
              replace: true,
              state: { 
                message: "Failed to verify account status. Please try again." 
              }
            });
            return; // Exit early, don't render page
          }
        }
      } catch (_) {
        // ignore and keep page rendered
      }
      setIsChecking(false); // Only show content if Stripe not complete
    };
    refresh();
    
    // Cleanup timeout on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async () => {
    const linkPromise = await dispatch(createConnectedAccount()).unwrap();
    window.location.href = linkPromise?.data;
  };

  // Show loader while checking to prevent dashboard flash
  if (isChecking) {
    return <Loader />;
  }

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <Header />
      <div className="sm:w-[80%] md:w-[70%] lg:w-[60%] py-20">
        <p className="font-poppins font-[500] text-[28px] text-center text-[#303030]">
          Complete Your Account Setup
        </p>
        <p className="font-poppins font-[500] text-[18px] text-start text-[#303030] mt-10">
          Account Setup
        </p>
        <p className="font-poppins font-[400] text-[14px] text-start text-[#767676] mt-2">
          It appears that your account setup is not yet complete. We require
          additional information about your business, along with the necessary
          legal documents. Kindly finalize your account details to gain access
          to the Swoodle fundraising portal and begin utilizing its features.
        </p>
        <div className="mt-20 flex justify-center items-center">
          <IncompleteImg />
        </div>
        <div className="w-full text-end mt-20">
          <PrimaryButton
            className={
              "w-[30%] font-montserrat text-[14px] text-center font-[500] h-[45px] rounded-[5px]"
            }
            label="Complete Account Setup"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default CompleteSetup;